import { assign, fromPromise, setup } from 'xstate';
import { TimeZone } from 'domains/timezone';
import { getConsultationAvailableTimes } from 'domains/calendars/queries';
import { AvailableTimes } from 'domains/calendars/types';
import { DEFAULT_APPOINTMENT_TYPE } from 'domains/signup/data/queryParams';
import { SignupMachineInput } from 'domains/signup/signupFlow/signupMachine';
import { MachineStateType } from 'utils/xstate/types';

export type CompletedScheduleMachineContext = {
  availableConsultDates: AvailableTimes;
  leadID: string;
  consultationDate: Date;
  consultationTimezone: TimeZone;
};

export type ScheduleResult = {
  data: CompletedScheduleMachineContext;
  earlyExit?: boolean;
};
export type ScheduleMachineInput = {
  machineConfig: Pick<SignupMachineInput, 'calendarID'> &
    Required<Pick<SignupMachineInput, 'appointmentType'>> & {
      canExitByBack?: boolean;
    };
};

type InternalContext = {
  data: Partial<CompletedScheduleMachineContext>;
  machineConfig: ScheduleMachineInput['machineConfig'];
  earlyExit?: boolean;
};

/** @xstate-layout N4IgpgJg5mDOIC5SwMYAtIFcA2YCyAhugJYB2YAdGKQC5gBOAwgPamw40E3GsAiXYAMTsARgFtiNFmw5cepfnQDaABgC6iUAAdmsSfM0gAHogC0AFgDsligCYVAVgAcANhUBGR+YCcv8wBoQAE9EAGZbbwpvd1tbF1Cnb3sI2wBfVMDUDAgcfCI0MkoUVgAzYnoxaXZsTm4+AUERIgBrVQ0kEB09OtJDEwRTS3cXCndLJ3NJ0KSnWwTAkIRQh1sKeKdZ83dvaxVLbwd0zPQsXEIScgpi0jKKqtkexSFr27FIe5q5euV1Qy79Vh9MzmRIUaahULmUIqKGxHwLRDxVZOSzmBz7FxOFS2dzuI4gLKnPIXSjYZgECBkKAAQQAbgRiNgCCJcE9YIIIKxKGRacxmpRCTkzvlChQyRSqXSGUyWWA2QgecwUF9SG02n9dADeh1+hZvCoKIkIeYXJZnOjxgiBjjRpCDt4JjCrC40hkCSchcSCpdxZTSDT6YzmayBOyGPRmPQKFomTQSpGxBRBblzt7SeS-QHpcG5aGFaRecqemrfh1-j0gQN3KERtssd5JujQnWnFbTDEbLtnNtbJsJk50m7SMwIHBDMnhSSNd0DDqzJYXOZRuNJlCZnNW8EzLZzAbbJZHE4HA5HLEHGj8ROvaLqHQmKxqrV5E9p1rKxYXJEHJ4HOEHJjsTmSwrWGUI1jmb8QQ2U0IRcS8PRTEVLhecpKgfB5nwEV8KznAYrCcOxqxceJzxcYZzFsK1v1WRdXDRM1pgdQ43SvVNRV9SVAxlEM6HgMtNRw0BdRPJdd1xVx1ksexvBcNt3BWCgu0SFRMVRBd4OyRCSTFDMqTZAAxaVIGw2chLMTxVlhNEXGcDw9jNK0DRUaI3BNSxlmGFQUQcbwNKJNjLjKUhiFgbITMBXCLFCGxwmhfcQXCJxjStcYHFGF1YgSHEpNcQdUiAA */
export const scheduleMachineV2 = setup({
  types: {
    context: {} as InternalContext,
    events: {} as
      | { type: 'back' }
      | { type: 'submitConsultationDate'; consultationDate: Date; consultationTimezone: TimeZone }
      | { type: 'confirmedConsultationDate' },

    input: {} as ScheduleMachineInput,
    output: {} as ScheduleResult,
  },

  actions: {
    saveConsultationDate: assign(({ context }, params: { consultationDate: Date; consultationTimezone: TimeZone }) => ({
      data: {
        ...context.data,
        consultationDate: params.consultationDate,
        consultationTimezone: params.consultationTimezone,
      },
    })),
    saveAvailableConsultDates: assign(({ context }, params: { data: AvailableTimes }) => ({
      data: {
        ...context.data,
        availableConsultDates: params.data,
      },
    })),
  },

  actors: {
    getAvailableConsultDates: fromPromise(
      async ({ input }: { input: { calendarID?: string; appointmentType: string } }) => {
        return getConsultationAvailableTimes({
          appointmentType: input.appointmentType,
          calendarID: input.calendarID,
        });
      },
    ),
  },
  guards: {
    canExitEarly: ({ context }) => context.machineConfig.canExitByBack === true,
  },
}).createMachine({
  id: 'scheduleMachine',
  initial: 'loadingAvailableDates',

  context: ({
    input: {
      machineConfig: { appointmentType, calendarID, canExitByBack },
    },
  }) => ({
    machineConfig: {
      appointmentType: appointmentType ?? DEFAULT_APPOINTMENT_TYPE,
      calendarID: calendarID,
      canExitByBack,
    },
    data: {},
    earlyExit: false,
  }),

  states: {
    enterConsultationDate: {
      on: {
        submitConsultationDate: {
          target: 'confirmConsultationDate',

          actions: {
            type: 'saveConsultationDate',
            params: ({ event: { consultationDate, consultationTimezone } }) => ({
              consultationDate,
              consultationTimezone,
            }),
          },
        },

        back: { target: 'finished', actions: assign({ earlyExit: true }), guard: 'canExitEarly' },
      },
    },

    confirmConsultationDate: {
      on: {
        back: 'enterConsultationDate',
        confirmedConsultationDate: 'finished',
      },
    },

    loadingAvailableDates: {
      invoke: {
        src: 'getAvailableConsultDates',
        input: ({
          context: {
            machineConfig: { appointmentType, calendarID },
          },
        }) => ({ appointmentType: appointmentType, calendarID }),
        onDone: {
          target: 'enterConsultationDate',
          actions: {
            type: 'saveAvailableConsultDates',
            params: ({ event: { output } }) => ({
              data: output,
            }),
          },
        },
        onError: 'loadingDatesFailed',
      },
    },

    loadingDatesFailed: {},

    finished: {
      type: 'final',
    },
  },
  output: ({ context }) => context as ScheduleResult,
});

export type ScheduleState = MachineStateType<typeof scheduleMachineV2>;
